declare const __POSTHOG_KEY__: string
// Injected by the esbuild define at bundle time; absent under vitest (which
// imports this transitively via hook.ts) — and analyticsEvent already no-ops
// on an empty key, so tolerating absence is both test- and runtime-safe.
const KEY = typeof __POSTHOG_KEY__ !== 'undefined' ? __POSTHOG_KEY__ : ''
const HOST = 'https://us.i.posthog.com'
const pending = new Set<Promise<void>>()

function withTimeout(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs)
    promise.finally(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

export function sanitizeAnalyticsMessage(message: unknown): string {
  return String(message ?? 'unknown')
    .replace(/\/Users\/[^/\s]+\/[^\s)'"`]+/g, '[path]')
    .replace(/[A-Za-z]:\\Users\\[^\\\s]+\\[^\s)'"`]+/g, '[path]')
    .slice(0, 180)
}

export function analyticsEvent(
  event: string,
  distinctId: string,
  props: Record<string, unknown> = {},
): Promise<void> {
  if (!KEY || !distinctId) return Promise.resolve()
  // Fire and forget — never blocks the command
  const request = fetch(`${HOST}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: KEY,
      event,
      distinct_id: `workspace:${distinctId}`,
      properties: { ...props, source: 'cli', $lib: 'driftless-cli' },
    }),
  })
    .then(() => undefined)
    .catch(() => undefined)
  pending.add(request)
  request.finally(() => pending.delete(request))
  return request
}

export function analyticsCommandError(
  command: string,
  distinctId: string | undefined,
  message: unknown,
  meta: Record<string, unknown> = {},
): Promise<void> {
  if (!distinctId) return Promise.resolve()
  // Drop undefined props so PostHog only stores fields we actually have.
  const cleanMeta = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined))
  return analyticsEvent('cli_command_error', distinctId, {
    command,
    message: sanitizeAnalyticsMessage(message),
    ...cleanMeta,
  })
}

export async function flushAnalytics(timeoutMs = 300): Promise<void> {
  if (pending.size === 0) return
  await withTimeout(Promise.allSettled([...pending]), timeoutMs)
}

export async function flushAndExit(code: number, timeoutMs = 300): Promise<never> {
  await flushAnalytics(timeoutMs)
  process.exit(code)
}
